import './retentionChart.scss'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceDot,
} from 'recharts'
import { useRetentionChartData } from '../../hooks'
import { CustomLegend } from '@/features/analytics/components'

export type RetentionChartProps = {
    channelId: number
}
export const RetentionChart = ({ channelId }: RetentionChartProps) => {
    const { retentionData, inviteLinks, lastIndices } = useRetentionChartData({
        channelId,
    })

    return (
        <div className="retention-chart">
            <div className="retention-chart__container">
                <ResponsiveContainer width="100%" height={340}>
                    <LineChart
                        margin={{ top: 20, right: 60, left: 20, bottom: 5 }}
                        data={retentionData}
                    >
                        <CartesianGrid strokeDasharray="0 0" stroke="#e0e0e0" />
                        <XAxis
                            dataKey="index"
                            label={{
                                value: '',
                                position: 'insideBottom',
                                offset: -5,
                                fill: 'var(--text-color)',
                            }}
                            tick={{
                                fill: 'var(--text-color)',
                                fontSize: 12,
                            }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 100]}
                            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                            tickFormatter={(value) => `${value}%`}
                            label={{
                                value: '',
                                angle: -90,
                                position: 'insideLeft',
                                fill: 'var(--text-color)',
                                fontSize: 12,
                            }}
                            tick={{
                                fill: 'var(--text-color)',
                                fontSize: 12,
                            }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            formatter={(
                                value?: number,
                                name?: string,
                                props?: any
                            ) => [
                                `${(value ?? 0).toFixed(2)}%`,
                                `${name ?? ''} (${props.payload.index + 1})`,
                            ]}
                            contentStyle={{
                                backgroundColor: 'var(--white)',
                                border: '1px solid var(--stroke)',
                                borderRadius: '8px',
                                boxShadow: 'var(--box-shadow)',
                            }}
                            labelStyle={{
                                color: 'var(--text-color)',
                                fontWeight: 'bold',
                            }}
                            itemStyle={{
                                color: 'var(--text-color)',
                            }}
                        />
                        {inviteLinks.map((link) => (
                            <Line
                                key={link.id}
                                type="monotone"
                                dataKey={link.id}
                                name={link.name}
                                stroke={link.color}
                                strokeWidth={4}
                                dot={{
                                    r: 4,
                                    stroke: link.color,
                                    strokeWidth: 2,
                                    fill: link.color,
                                }}
                                activeDot={{
                                    r: 4,
                                    stroke: link.color,
                                    strokeWidth: 2,
                                    fill: link.color,
                                }}
                                isAnimationActive={false}
                                connectNulls={true}
                            />
                        ))}
                        {inviteLinks.map((link) => {
                            const lastIndex = lastIndices[link.id]
                            if (
                                lastIndex !== -1 &&
                                retentionData[lastIndex][link.id] !== undefined
                            ) {
                                const entry = retentionData[lastIndex]
                                return (
                                    <ReferenceDot
                                        key={`dot-${link.id}-last`}
                                        x={entry.index}
                                        y={entry[link.id]}
                                        r={18}
                                        fill={link.color}
                                        label={{
                                            value: `${entry[link.id].toFixed(0)}%`,
                                            fill: '#000',
                                            fontSize: 12,
                                        }}
                                    />
                                )
                            }
                            return null
                        })}
                    </LineChart>
                </ResponsiveContainer>
                <CustomLegend payload={inviteLinks} channelId={channelId} />
            </div>
        </div>
    )
}
